import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { statsData, categories } from '@/mocks/articles';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('submitting');
    setFormError('');
    try {
      const res = await fetch('https://readdy.ai/api/form/d96caisut53jbls7kmb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([k]) => k !== 'website_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg.toLowerCase().includes('spam')) {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="爆瓜网 - 中国领先的娱乐八卦资讯聚合平台。专注网红大瓜、明星黑料、博主反差视频，为千万吃瓜群众提供最真实的爆料内容。了解我们的品牌故事、团队理念和联系方式。"
        keywords="爆瓜网,关于爆瓜网,吃瓜平台,爆料平台,品牌介绍,联系方式,商务合作"
        canonicalUrl="https://baogua.com/about"
        ogType="website"
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于爆瓜网',
            description: '了解爆瓜网品牌故事、平台数据与联系方式',
            url: 'https://baogua.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '爆瓜网',
              url: 'https://baogua.com',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baogua.com' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://baogua.com/about' },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '爆瓜网',
            url: 'https://baogua.com',
            description: '中国领先的娱乐八卦资讯聚合平台',
            foundingDate: '2019',
            contactPoint: [
              {
                '@type': 'ContactPoint',
                contactType: '商务合作',
                email: 'business@baogua.com',
              },
              {
                '@type': 'ContactPoint',
                contactType: '爆料投稿',
                email: 'tips@baogua.com',
              },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '爆瓜网是什么？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '爆瓜网是中国领先的娱乐八卦资讯聚合平台，专注网红大瓜、明星黑料、博主反差视频等实时爆料内容，是每日吃瓜追更的首选站点入口。',
                },
              },
              {
                '@type': 'Question',
                name: '如何下载爆瓜网APP？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以通过本页面提供的iOS和Android下载按钮获取爆瓜网APP，或扫描页面上的二维码进行下载。爆瓜网APP支持实时推送通知，让您第一时间获取最新爆料。',
                },
              },
              {
                '@type': 'Question',
                name: '如何向爆瓜网投稿爆料？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以通过关于我们页面的"爆料投稿"表单提交您的爆料内容，或发送邮件至tips@baogua.com。我们的编辑团队会在24小时内审核并回复。',
                },
              },
              {
                '@type': 'Question',
                name: '爆瓜网的爆料内容是否经过核实？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的。爆瓜网编辑团队对所有爆料内容进行多维度核实，包括交叉验证信息来源、核查视频/图片真实性、比对多方说法等。我们坚持"让真相跑赢公关"的原则，只发布经过审核的真实内容。',
                },
              },
            ],
          },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative h-[280px] md:h-[400px] w-full overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20entertainment%20media%20concept%20with%20flowing%20curves%20of%20warm%20light%20trails%2C%20dark%20cinematic%20background%2C%20soft%20golden%20and%20amber%20glows%2C%20digital%20newsroom%20aesthetic%2C%20elegant%20atmospheric%20composition%2C%20high%20contrast%2C%20editorial%20style&width=1920&height=1080&seq=about-hero-01&orientation=landscape"
              alt="关于爆瓜网 - 中国吃瓜娱乐第一入口"
              title="爆瓜网品牌形象"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>
            <div className="relative z-10 h-full w-full flex items-end pb-8 md:pb-12">
              <div className="px-4 md:px-6 w-full">
                <div className="max-w-7xl mx-auto">
                  <span className="inline-block px-3 py-1 border border-white/40 rounded-full text-[10px] md:text-xs text-white/90 font-label mb-3 md:mb-4">
                    关于爆瓜
                  </span>
                  <h1 className="text-2xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight tracking-tight">
                    中国吃瓜娱乐
                    <br />
                    第一入口
                  </h1>
                </div>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background-50 to-transparent"></div>
          </section>

          <div className="px-4 md:px-6 py-4 md:py-5 bg-background-100/50 border-b border-background-200/50">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 font-label" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                <span className="text-foreground-800 font-medium">关于我们</span>
              </nav>
            </div>
          </div>

          <section className="py-14 md:py-20 bg-secondary-50">
            <div className="px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col items-center text-center mb-10 md:mb-14">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-white text-foreground-800 text-xs font-semibold font-label mb-4">
                    品牌简介
                  </span>
                  <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                    专注娱乐爆料六年
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-foreground-600 font-body">
                    构建全网吃瓜中枢，为千万吃瓜群众提供最真实的爆料内容
                  </p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-12 md:mb-16">
                  <div className="text-center">
                    <div className="w-12 h-12 md:w-14 md:h-14 mx-auto flex items-center justify-center rounded-full bg-white mb-3">
                      <i className="ri-user-smile-line text-xl text-secondary-600"></i>
                    </div>
                    <p className="text-xs md:text-sm text-foreground-700 font-body">网红吃瓜</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 md:w-14 md:h-14 mx-auto flex items-center justify-center rounded-full bg-white mb-3">
                      <i className="ri-star-line text-xl text-secondary-600"></i>
                    </div>
                    <p className="text-xs md:text-sm text-foreground-700 font-body">明星黑料</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 md:w-14 md:h-14 mx-auto flex items-center justify-center rounded-full bg-white mb-3">
                      <i className="ri-alert-line text-xl text-secondary-600"></i>
                    </div>
                    <p className="text-xs md:text-sm text-foreground-700 font-body">博主翻车</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 md:w-14 md:h-14 mx-auto flex items-center justify-center rounded-full bg-white mb-3">
                      <i className="ri-flashlight-line text-xl text-secondary-600"></i>
                    </div>
                    <p className="text-xs md:text-sm text-foreground-700 font-body">实时更新</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                  <div className="text-center">
                    <p className="text-3xl md:text-5xl font-heading font-black text-foreground-950 tracking-tight">2019</p>
                    <p className="mt-1 text-sm font-semibold text-foreground-800">创始年份</p>
                    <p className="mt-1 text-xs text-foreground-600 font-body">六年来深耕娱乐爆料</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl md:text-5xl font-heading font-black text-foreground-950 tracking-tight">100万+</p>
                    <p className="mt-1 text-sm font-semibold text-foreground-800">日活用户</p>
                    <p className="mt-1 text-xs text-foreground-600 font-body">每日活跃吃瓜群众</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl md:text-5xl font-heading font-black text-foreground-950 tracking-tight">{statsData[0].value}</p>
                    <p className="mt-1 text-sm font-semibold text-foreground-800">独家爆料</p>
                    <p className="mt-1 text-xs text-foreground-600 font-body">累计收录真实爆料内容</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl md:text-5xl font-heading font-black text-foreground-950 tracking-tight">50+</p>
                    <p className="mt-1 text-sm font-semibold text-foreground-800">合作媒体</p>
                    <p className="mt-1 text-xs text-foreground-600 font-body">深度合作的媒体平台</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
                <div className="lg:w-3/5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                    <span className="text-xs font-semibold text-foreground-800 font-label tracking-wide">主编寄语</span>
                  </div>
                  <blockquote className="text-xl md:text-3xl font-heading font-bold text-foreground-950 leading-relaxed">
                    &ldquo;让真相跑赢公关，吃瓜不等于网暴，我们只搬运实锤。
                    <br />
                    每一个爆料背后，都有瓜友的信任与期待。&rdquo;
                  </blockquote>
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-800">爆瓜网主编</span>
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-verified-badge-fill text-accent-500 text-sm"></i>
                    </span>
                    <span className="text-xs text-foreground-500 font-label">编辑团队</span>
                  </div>
                </div>
                <div className="lg:w-2/5 flex justify-center">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20editor%20in%20chief%20portrait%20in%20black%20and%20white%2C%20newsroom%20director%20silhouette%2C%20shoulder%20up%20composition%2C%20looking%20upward%20with%20confidence%2C%20cinematic%20studio%20lighting%2C%20clean%20white%20background%2C%20high%20contrast%2C%20editorial%20aesthetic%2C%20documentary%20style&width=600&height=800&seq=about-editor-01&orientation=portrait"
                    alt="爆瓜网主编"
                    title="爆瓜网主编寄语"
                    className="w-40 h-52 md:w-52 md:h-72 object-cover object-top rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="mb-10 md:mb-14">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 text-center">
                  核心板块
                </h2>
                <p className="mt-2 text-sm md:text-base text-foreground-600 font-body text-center">
                  全面覆盖
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {[
                  {
                    ...categories[0],
                    image: 'https://readdy.ai/api/search-image?query=Dramatic%20entertainment%20award%20stage%20with%20spotlight%20and%20red%20carpet%2C%20cinematic%20atmosphere%2C%20warm%20golden%20lighting%2C%20editorial%20photography%2C%20elegant%20composition&width=800&height=600&seq=about-cat-star-scandal&orientation=landscape',
                  },
                  {
                    ...categories[1],
                    image: 'https://readdy.ai/api/search-image?query=Social%20media%20influencer%20studio%20setup%20with%20ring%20light%20and%20smartphone%20on%20tripod%2C%20modern%20creative%20workspace%2C%20warm%20neutral%20tones%2C%20editorial%20product%20photography%2C%20clean%20composition&width=800&height=600&seq=about-cat-influencer-gossip&orientation=landscape',
                  },
                  {
                    ...categories[2],
                    image: 'https://readdy.ai/api/search-image?query=Dramatic%20behind%20the%20scenes%20video%20production%20setup%20with%20camera%20equipment%20and%20monitor%20display%2C%20documentary%20filmmaking%20aesthetic%2C%20warm%20studio%20lighting%2C%20creative%20workspace%20atmosphere&width=800&height=600&seq=about-cat-blogger-flip&orientation=landscape',
                  },
                ].map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 bg-background-50 cursor-pointer"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-background-100 flex items-center justify-center">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        title={cat.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {cat.name}
                      </h3>
                      <p className="mt-2 text-xs md:text-sm text-foreground-600 font-body line-clamp-2">
                        {cat.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="flex flex-col lg:flex-row min-h-[500px] md:min-h-[600px]">
            <div className="lg:w-1/2 relative overflow-hidden bg-accent-50">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20laptop%20and%20coffee%20on%20clean%20desk%2C%20warm%20natural%20light%20streaming%20through%20window%2C%20creative%20agency%20aesthetic%2C%20minimalist%20interior%2C%20editorial%20photography%2C%20soft%20shadows%2C%20elegant%20composition&width=960&height=800&seq=about-contact-01&orientation=portrait"
                alt="爆瓜网商务合作"
                title="联系我们 - 商务合作与爆料投稿"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-6 left-4 md:bottom-10 md:left-8">
                <h3 className="text-2xl md:text-4xl font-heading font-black text-foreground-950 leading-tight">
                  商务合作
                  <br />
                  爆料投稿
                </h3>
              </div>
            </div>

            <div className="lg:w-1/2 flex flex-col justify-center px-4 md:px-6 py-12 md:py-16 bg-background-50">
              <div className="max-w-md mx-auto w-full">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 text-center">
                  与我们联系
                </h2>
                <p className="mt-3 text-sm md:text-base text-foreground-600 font-body text-center">
                  商务合作、爆料投稿、广告咨询，欢迎通过下方表单联系我们
                </p>

                <div className="mt-6 flex flex-col gap-3 text-sm text-foreground-700 font-body text-center">
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-mail-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                    商务合作：business@baogua.com
                  </span>
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                    爆料投稿：tips@baogua.com
                  </span>
                </div>

                <form
                  className="mt-8 flex flex-col gap-4"
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="contact-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-700 font-label mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-white text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors font-body"
                        placeholder="您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-700 font-label mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-white text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors font-body"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 font-label mb-1.5">主题</label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-white text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors font-body cursor-pointer"
                    >
                      <option value="">请选择主题</option>
                      <option value="商务合作">商务合作</option>
                      <option value="爆料投稿">爆料投稿</option>
                      <option value="广告咨询">广告咨询</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 font-label mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-white text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors font-body resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 font-label mt-1 text-right">最多500字</p>
                  </div>
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-6 py-3 bg-foreground-950 text-background-50 font-semibold text-sm rounded-full hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                    <i className="ri-send-plane-fill ml-2"></i>
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 font-body text-center bg-accent-50 rounded-lg py-3 px-4">
                      消息已发送！感谢您的来信，我们会尽快回复。
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-600 font-body text-center bg-red-50 rounded-lg py-3 px-4">
                      {formError || '发送失败，请稍后重试'}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}